/* eslint-disable react/jsx-sort-props */
import { alpha } from '@mui/material/styles';
import {
  CHART_HEIGHT,
  ChartEmpty,
} from '../RelatorioDigitacao/chartShared';
import { formatPeriodoChartAxisTick } from './constants';

export { CHART_HEIGHT, ChartEmpty };

export const getMaterialChartLegendProps = () => ({
  direction: 'row',
  position: { vertical: 'bottom', horizontal: 'middle' },
  padding: 0,
  sx: {
    gap: 8,
    pt: 0.5,
    pb: 0,
    '.MuiChartsLegend-series': {
      gap: '6px',
    },
  },
});

export const mapMaterialToDataset = (items) =>
  (items || [])
    .map((item) => ({
      label: item.label || item.tipo || '—',
      tipo: item.tipo,
      totalKg: Number(item.totalKg) || 0,
    }))
    .filter((item) => item.totalKg > 0)
    .sort((a, b) => b.totalKg - a.totalKg);

const getThemeColorBases = (theme) => [
  theme.palette.primary.main,
  theme.palette.info.main,
  theme.palette.success.main,
  theme.palette.warning.main,
  theme.palette.secondary?.main ?? theme.palette.primary.dark,
  theme.palette.error.light,
];

export const getMaterialChartColor = (tipo, index, theme) => {
  const bases = getThemeColorBases(theme);
  const baseIndex = index != null && index >= 0
    ? index % bases.length
    : (() => {
        let hash = 0;
        const key = tipo || '';
        for (let i = 0; i < key.length; i += 1) {
          hash += key.charCodeAt(i);
        }
        return hash % bases.length;
      })();
  const shade = Math.floor((index ?? 0) / bases.length) % 3;
  const opacity = 0.92 - shade * 0.12;
  return alpha(bases[baseIndex], opacity);
};

const deriveMaterialColumnsFromItems = (items, materialKey = 'byMaterial') => {
  const tipoSet = new Map();
  (items || []).forEach((item) => {
    const byMaterial = item[materialKey] || item.pesos || {};
    Object.keys(byMaterial).forEach((tipo) => {
      if (!tipoSet.has(tipo)) {
        const label = item.materialLabels?.[tipo] || tipo;
        tipoSet.set(tipo, { tipo, label });
      }
    });
  });
  return Array.from(tipoSet.values());
};

export const resolveMaterialColumns = (items, materialColumns, materialKey = 'byMaterial') => {
  if (materialColumns?.length) return materialColumns;
  return deriveMaterialColumnsFromItems(items, materialKey);
};

export const buildPeriodoHistoricoModel = (items, materialColumns) => {
  const columns = resolveMaterialColumns(items, materialColumns);

  const dataset = (items || []).map((item) => {
    const byMaterial = item.byMaterial || item.pesos || {};
    const periodoLabel = item.periodoLabel || item.periodoShortLabel || '—';
    const row = {
      periodoLabel,
      periodoAxisLabel: formatPeriodoChartAxisTick(
        item.periodoInicio,
        item.periodoFim,
        item.periodoShortLabel || item.periodoLabel,
      ),
      periodoInicio: item.periodoInicio,
      periodoFim: item.periodoFim,
      totalKg: Number(item.totalKg) || 0,
    };
    columns.forEach((col) => {
      row[col.tipo] = Number(byMaterial[col.tipo]) || 0;
    });
    return row;
  });

  return { dataset, materialColumns: columns };
};

export const buildMalhaMaterialChartModel = (items, materialColumns) => {
  const columns = resolveMaterialColumns(items, materialColumns);

  const dataset = (items || []).map((item) => {
    const byMaterial = item.byMaterial || {};
    const row = {
      malhaLabel: item.malhaCodigo || item.malhaNome || '—',
      malhaId: item.malhaId,
      totalKg: Number(item.totalKg) || 0,
    };
    columns.forEach((col) => {
      row[col.tipo] = Number(byMaterial[col.tipo]) || 0;
    });
    return row;
  });

  return { dataset, materialColumns: columns };
};

export const buildMaterialBarSeries = (
  materialColumns,
  mode,
  theme,
  formatValue,
) =>
  (materialColumns || []).map((col, index) => ({
    dataKey: col.tipo,
    label: col.label || col.tipo,
    color: getMaterialChartColor(col.tipo, index, theme),
    stack: mode === 'stacked' ? 'peso' : undefined,
    valueFormatter: (value) => formatValue(value, col.label || col.tipo),
  }));

export const buildMalhaMaterialStackedSeries = (materialColumns, theme, formatValue) =>
  buildMaterialBarSeries(materialColumns, 'stacked', theme, formatValue);
